import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import * as React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Checkbox} from 'semantic-ui-react';

import {Analytics} from '../../services/analytics';
import DOC_URLS from '../../util/doc_urls';
import {useRampFlagFullFidelityRanges} from '../../util/rampFeatureFlags';
import {LabeledOptionBlock as LabeledOption} from '../elements/LabeledOptionBlock';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import ProjectFieldSelector from '../ProjectFieldSelector';
import {DEFAULT_LINE_PLOT_SETTINGS} from '../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {AggRange} from './AggRange';
import {AggType} from './AggType';
import {PlotType} from './PlotType';
import {type BarChartConfig, type PanelGroupingOptionsProps} from './types';

export const OutliersPanelGroupingOptions = (
  props: PanelGroupingOptionsProps
) => {
  const {config, disabled, isGrouped, updateConfig, usingExpressions} = props;
  const {isSingleRun} = useSingleRunModeContext();
  const allowFullFidelityRanges =
    useRampFlagFullFidelityRanges(props.pageQuery.entityName) ?? false;

  const showAggregationOptions =
    (props.type === 'bars' &&
      (config as BarChartConfig).plotStyle !== 'boxplot' &&
      (config as BarChartConfig).plotStyle !== 'violin') ||
    props.type === 'lines';
  const disableAggregationOptions = props.type === 'lines' && usingExpressions;

  return (
    <Tailwind>
      {isSingleRun ? (
        <>
          {(config.metrics == null || config.metrics.length <= 1) && (
            <>
              <p>Select multiple metrics to enable grouping</p>
            </>
          )}
        </>
      ) : (
        <>
          <LabeledOption
            label="Group runs"
            helpText="Aggregate over runs"
            docUrl={DOC_URLS.compareMetrics + '#grouping'}
            option={
              <Switch.Root
                size="medium"
                disabled={disabled}
                data-test={'checkbox-aggregate'}
                checked={config.aggregate}
                name="aggregate"
                onCheckedChange={isChecked =>
                  updateConfig({aggregate: isChecked})
                }>
                <Switch.Thumb
                  size="medium"
                  checked={config.aggregate ?? false}
                />
              </Switch.Root>
            }
          />
          {config.aggregate && (
            <LabeledOption
              label="Group by"
              helpText="Group by a run attribute"
              option={
                <>
                  <ProjectFieldSelector
                    className={'groupby-picker'}
                    disabled={!props.config.aggregate}
                    query={props.pageQuery}
                    selection
                    columns={['config']}
                    defaultKeys={['config:None']}
                    value={`config:${config.groupBy ?? 'None'}`}
                    multi={false}
                    searchByKeyAndText
                    setValue={value =>
                      updateConfig({
                        groupBy: value.split(':')[1],
                      })
                    }
                  />
                </>
              }
            />
          )}
        </>
      )}
      {(config.aggregateMetrics === true ||
        ((props.type === 'bars' || props.type === 'lines') &&
          config.metrics != null &&
          config.metrics.length > 1)) && (
        <LabeledOption
          label="Metrics"
          helpText="Aggregate over all metrics"
          option={
            <Checkbox
              toggle
              disabled={disabled}
              checked={config.aggregateMetrics}
              name="aggregate"
              onChange={(e, value) =>
                updateConfig({
                  aggregateMetrics: value.checked,
                })
              }
            />
          }
        />
      )}
      {isGrouped && (
        <>
          <div className="group-options">
            <div className="group-option">
              {usingExpressions && (
                <div className="hint-text">
                  <p>
                    Grouping functions are disabled while using expressions. In
                    order to reenable, remove all expressions.
                  </p>
                </div>
              )}
              {props.type === 'bars' && (
                <PlotType
                  config={config as BarChartConfig}
                  usingExpressions={usingExpressions}
                  updateConfig={updateConfig}
                />
              )}

              {showAggregationOptions && (
                <AggType
                  config={config}
                  updateConfig={updateConfig}
                  options={{
                    allowMedian: false,
                    defaultGroupAgg: DEFAULT_LINE_PLOT_SETTINGS['groupAgg'],
                    disabled: disableAggregationOptions,
                    includeSum: props.type === 'scalar',
                  }}
                />
              )}

              {showAggregationOptions && allowFullFidelityRanges && (
                <AggRange
                  config={config}
                  disabled={disableAggregationOptions}
                  updateConfig={newConfig => {
                    updateConfig(newConfig);
                    Analytics.track('Aggregation Range Changed', {
                      groupArea: newConfig.groupArea,
                      entityName: props.pageQuery.entityName,
                      projectName: props.pageQuery.projectName,
                    });
                  }}
                  includeSamples={false}
                  defaultOption="minmax"
                />
              )}
            </div>
          </div>
        </>
      )}
    </Tailwind>
  );
};
